import { ReactNode, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import AppDialog, { DialogSize } from "./AppDialog";

export const CONFIRM_ERROR_FALLBACK = "Não foi possível concluir. Tente de novo.";

export type ConfirmDialogProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  context?: ReactNode;
  message: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => Promise<unknown>;
  errorMessage?: (error: unknown) => string;
  size?: DialogSize;
};

export default function ConfirmDialog({
  open,
  onClose,
  title,
  context,
  message,
  confirmLabel,
  cancelLabel = "Voltar",
  destructive = false,
  onConfirm,
  errorMessage,
  size = "sm",
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reabriu: começa sem o erro da tentativa anterior.
  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  // Só fecha no sucesso; no erro, a mensagem fica dentro do diálogo.
  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      setBusy(false);
      onClose();
    } catch (e) {
      setBusy(false);
      setError(errorMessage ? errorMessage(e) : CONFIRM_ERROR_FALLBACK);
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={title}
      context={context}
      size={size}
      busy={busy}
      actions={
        <>
          <Button onClick={onClose} disabled={busy} sx={{ color: "text.primary" }}>
            {cancelLabel}
          </Button>
          <LoadingButton
            variant="contained"
            color={destructive ? "error" : "primary"}
            loading={busy}
            onClick={confirm}
          >
            {confirmLabel}
          </LoadingButton>
        </>
      }
    >
      <Stack spacing={1.5}>
        {typeof message === "string" ? <Typography>{message}</Typography> : message}
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
    </AppDialog>
  );
}
